import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
 
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { useAuth } from 'contexts/AuthContext'
import { useTheme } from 'contexts/ThemeContext'
import { authApi } from 'lib/api'
import { Mail, Lock, Eye, EyeOff, AlertCircle, ArrowRight } from 'lucide-react'

export function Login() {
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState(searchParams.get('email') || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [requires2FA, setRequires2FA] = useState(false)
  const [authenticatorAvailable, setAuthenticatorAvailable] = useState(false)
  const [emailAvailable, setEmailAvailable] = useState(false)
  const [selected2FAMethod, setSelected2FAMethod] = useState<'authenticator' | 'email'>('authenticator')
  const [showBackupCodeInput, setShowBackupCodeInput] = useState(false)
  const [otpToken, setOtpToken] = useState('')
  const [backupCode, setBackupCode] = useState('')
  const [passwordValidated, setPasswordValidated] = useState(false) // Track if password step is complete
  const [emailOTPSent, setEmailOTPSent] = useState(false)
  const [isResendingOTP, setIsResendingOTP] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  
  const { completeLogin } = useAuth()
  const { theme } = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const logoSrc = theme === 'dark' ? '/logodark512.png' : '/logo512.png'

  const apiErrorMessage = (err: any, fallback: string) =>
    err.response?.data?.error || err.response?.data?.detail || fallback

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) {
      return
    }

    const timer = window.setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => window.clearInterval(timer)
  }, [resendCooldown])

  // Auto-send email OTP if only email is available
  useEffect(() => {
    if (requires2FA && !authenticatorAvailable && emailAvailable && !emailOTPSent) {
      const sendEmailOTP = async () => {
        setIsResendingOTP(true)
        try {
          await authApi.requestEmailOTP(email)
          setEmailOTPSent(true)
          setResendCooldown(50)
        } catch (error: any) {
          setError(apiErrorMessage(error, 'Failed to send code'))
        } finally {
          setIsResendingOTP(false)
        }
      }
      sendEmailOTP()
    }
  }, [requires2FA, authenticatorAvailable, emailAvailable, emailOTPSent, email])

  const handleSwitch2FAMethod = async () => {
    if (!emailAvailable) {
      return
    }

    if (selected2FAMethod === 'authenticator') {
      setSelected2FAMethod('email')
      setOtpToken('')
      setError('')
      setIsResendingOTP(true)
      try {
        await authApi.requestEmailOTP(email)
        setEmailOTPSent(true)
        setResendCooldown(50)
      } catch (error: any) {
        setError(apiErrorMessage(error, 'Failed to send code'))
      } finally {
        setIsResendingOTP(false)
      }
    } else {
      setSelected2FAMethod('authenticator')
      setOtpToken('')
      setError('')
      setEmailOTPSent(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    // Drop any leftover JWTs from before cookie auth
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('selektia_user')

    try {
      // If we're in 2FA mode, verify OTP or backup code
      if (requires2FA) {
        if (showBackupCodeInput) {
          // Verify backup code
          try {
            const response = await authApi.verifyBackupCode(email, backupCode)
            if (response.data.user) {
              await completeLogin(response.data.user)

              if (rememberMe) {
                localStorage.setItem('remember_email', email)
              } else {
                localStorage.removeItem('remember_email')
              }
              navigate('/dashboard')
            }
          } catch (error: any) {
            setError(apiErrorMessage(error, 'Invalid backup code'))
            setBackupCode('')
          } finally {
            setIsLoading(false)
          }
        } else {
          // Verify OTP with selected method
          try {
            const response = await authApi.verifyOTP(email, otpToken, selected2FAMethod)
            if (response.data.user) {
              await completeLogin(response.data.user)

              if (rememberMe) {
                localStorage.setItem('remember_email', email)
              } else {
                localStorage.removeItem('remember_email')
              }
              navigate('/dashboard')
            }
          } catch (error: any) {
            setError(apiErrorMessage(error, 'Invalid OTP code'))
            setOtpToken('')
          } finally {
            setIsLoading(false)
          }
        }
        return
      }

      // Step 1: Normal login flow - validate email and password first
      const response = await authApi.login(email, password)

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        // Password validated, move to 2FA step
        setPasswordValidated(true)
        setRequires2FA(true)
        setAuthenticatorAvailable(response.data.authenticator_available || false)
        setEmailAvailable(response.data.email_available || false)
        // Default to authenticator if available, otherwise email
        setSelected2FAMethod(response.data.authenticator_available ? 'authenticator' : 'email')
        setEmailOTPSent(false) // Will be set when user chooses email method
        setIsLoading(false)
        setError('') // Clear any previous errors
        return
      }

      // No 2FA, proceed with normal login
      if (response.data.user) {
        await completeLogin(response.data.user)
        if (rememberMe) {
          localStorage.setItem('remember_email', email)
        } else {
          localStorage.removeItem('remember_email')
        }
        navigate('/dashboard')
      } else {
        setError(t('login.invalidCredentials'))
      }
    } catch (error: any) {
      setError(apiErrorMessage(error, t('login.loginError')))
    } finally {
      setIsLoading(false)
    }
  }

  // Prefill email if remembered
  useEffect(() => {
    try {
      const saved = localStorage.getItem('remember_email')
      if (saved) {
        setEmail(saved)
        setRememberMe(true)
      }
    } catch (err) {
      // no-op
    }
  }, [])

  return (
    <div className="min-h-screen bg-muted flex items-stretch">
      {/* Illustration Panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-blue-400/10 to-indigo-500/10 dark:from-primary/10 dark:via-blue-300/10 dark:to-indigo-400/10" />
        {/* Decorative vectors */}
        <svg className="absolute -top-16 -left-16 h-96 w-96 text-primary/20" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path fill="currentColor" d="M39.2,-68.9C50.3,-61.7,58.7,-50.1,66.9,-37.7C75.1,-25.3,83,-12.7,85.4,1.5C87.8,15.8,84.8,31.6,76.7,44.1C68.5,56.7,55.2,66,41.1,73.4C27,80.8,13.5,86.2,-0.4,86.9C-14.4,87.6,-28.8,83.5,-41.2,76.4C-53.6,69.3,-64,59.2,-72.1,47.4C-80.2,35.6,-86,22.1,-87.8,7.7C-89.6,-6.6,-87.4,-22,-81.3,-35.7C-75.1,-49.3,-65,-61.2,-52.6,-68C-40.2,-74.8,-20.1,-76.5,-2.1,-72.8C15.9,-69.1,31.8,-60.1,39.2,-68.9Z" transform="translate(100 100)" />
        </svg>
        <svg className="absolute -bottom-24 -right-16 h-[28rem] w-[28rem] text-indigo-400/20" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path fill="currentColor" d="M48.6,-61.6C61.6,-51.4,70.8,-35.2,76.1,-17.4C81.3,0.4,82.6,19.7,75.2,35.6C67.8,51.5,51.7,64,35.1,71.6C18.6,79.2,1.7,82,-14.6,79.9C-30.9,77.8,-46.6,70.7,-59.2,59.2C-71.8,47.7,-81.3,31.8,-83.9,14.5C-86.5,-2.9,-82.2,-21.4,-72.7,-36.2C-63.2,-51,-48.5,-62.1,-33.1,-71.5C-17.8,-81,-8.9,-88.8,3.6,-94.7C16.2,-100.6,32.4,-104.7,48.6,-61.6Z" transform="translate(100 100)" />
        </svg>
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.07]">
          <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
                <path d="M 32 0 L 0 0 0 32" fill="none" stroke="currentColor" className="text-foreground" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        {/* Content */}
        <div className="relative z-10 p-12 xl:p-16 flex flex-col justify-center max-w-xl">
          <div className="mb-2">
            <img src={logoSrc} alt="Selektia" className="h-24 w-auto" />
          </div>
          <h2 className="text-4xl font-semibold leading-tight">
            {t('login.welcome')}
          </h2>
          <p className="mt-4 text-muted-foreground">
            {t('login.subtitle')}
          </p>
          
        </div>
      </div>

      {/* Form Panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-lg">
          <div className="mb-8 lg:hidden text-center">
            <div className="mb-3 flex justify-center">
              <img src={logoSrc} alt="Selektia" className="h-12 w-auto" />
            </div>
            <h1 className="text-xl font-semibold">{t('login.welcome')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('login.subtitle')}</p>
          </div>

          <div className="">
            <h2 className="text-center font-semibold tracking-tight mb-1">
              {passwordValidated && requires2FA 
                ? 'Two-Factor Authentication'
                : t('login.title')
              }
            </h2>
            <p className="text-center text-sm text-muted-foreground mb-4">
              {passwordValidated && requires2FA
                ? 'Enter your authentication code to complete login'
                : t('login.subtitle')
              }
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center space-x-2 p-3 bg-destructive/10 rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-600">{error}</span>
                </div>
              )}

              {/* Email field - only shown in password step */}
              {!passwordValidated && (
                <div className="space-y-1">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('login.emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
              )}

              {!passwordValidated ? (
                // Step 1: Email and Password
                <div className="space-y-1">
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('login.passwordPlaceholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 h-11"
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              ) : requires2FA ? (
                // Step 2: 2FA Verification (OTP or Backup Code)
                <>
                  {!showBackupCodeInput ? (
                    // OTP Input with single field and toggle
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
        <label htmlFor="otp-code" className="text-sm font-medium text-foreground">
          {selected2FAMethod === 'email' ? 'Enter code sent to your email' : 'Enter code from authenticator app'}
        </label>
        {emailAvailable && selected2FAMethod !== 'email' && (
                          <button
                            type="button"
                            onClick={handleSwitch2FAMethod}
                            className="text-xs text-primary hover:underline font-medium disabled:opacity-50"
                            disabled={isResendingOTP}
                          >
            Use email instead
                          </button>
                        )}
                      </div>
                      <div className="relative">
                        {selected2FAMethod === 'email' ? (
                          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        ) : (
                          <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        )}
                        <Input
                          id="otp-code"
                          type="text"
                          placeholder="000000"
                          value={otpToken}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                            setOtpToken(value)
                          }}
                          className="pl-10 h-11 text-center text-lg tracking-widest font-mono"
                          maxLength={6}
                          required
                          autoFocus
                        />
                      </div>
                      {selected2FAMethod === 'email' && (
                      <div className="mt-1">
                          <button
                            type="button"
                            onClick={async () => {
                              setIsResendingOTP(true)
                              setError('')
                              try {
                                await authApi.requestEmailOTP(email)
                                setEmailOTPSent(true)
                              setResendCooldown(50)
                              } catch (error: any) {
                                setError(apiErrorMessage(error, 'Failed to resend code'))
                              } finally {
                                setIsResendingOTP(false)
                              }
                            }}
                          disabled={isResendingOTP || resendCooldown > 0}
                          className="text-xs text-primary hover:underline disabled:opacity-50"
                          >
                          {isResendingOTP
                            ? 'Sending...'
                            : resendCooldown > 0
                              ? `Resend in ${resendCooldown}s`
                              : "Didn't receive code? Resend"}
                          </button>
                        </div>
                      )}

                      {/* Navigation and Options */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <button
                          type="button"
                          onClick={() => {
                            // Go back to password step
                            setPasswordValidated(false)
                            setRequires2FA(false)
                            setOtpToken('')
                            setBackupCode('')
                            setShowBackupCodeInput(false)
                            setError('')
                            setPassword('')
                            setEmailOTPSent(false)
                            setSelected2FAMethod('authenticator')
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          ← Back to password
                        </button>
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            onClick={() => {
                              setShowBackupCodeInput(true)
                              setOtpToken('')
                              setError('')
                            }}
                            className="text-xs text-muted-foreground hover:text-foreground"
                          >
                            Use backup code
                          </button>
                          <Link
                            to={`/recover-2fa?email=${encodeURIComponent(email)}`}
                            className="text-xs text-muted-foreground hover:text-foreground"
                          >
                            Lost access?
                          </Link>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Backup Code Input
                    <div className="space-y-1">
                      <label htmlFor="backupCode" className="text-sm font-medium text-muted-foreground">
                        Enter 8-character backup code
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="backupCode"
                          type="text"
                          placeholder="XXXXXXXX"
                          value={backupCode}
                          onChange={(e) => {
                            // Only allow alphanumeric, uppercase, max 8
                            const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
                            setBackupCode(value)
                          }}
                          className="pl-10 h-11 text-center text-lg tracking-widest font-mono"
                          maxLength={8}
                          required
                          autoFocus
                        />
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            setShowBackupCodeInput(false)
                            setBackupCode('')
                            setError('')
                          }}
                          className="text-xs text-primary hover:underline"
                        >
                          ← Back to OTP
                        </button>
                        <Link
                          to={`/recover-2fa?email=${encodeURIComponent(email)}`}
                          className="text-xs text-primary hover:underline"
                        >
                          Lost access? Recover
                        </Link>
                      </div>
                    </div>
                  )}
                </>
              ) : null}

              {/* Options row - only show in password step */}
              {!passwordValidated && (
                <div className="flex items-center justify-between pt-1">
                  <label className="inline-flex items-center gap-2 text-sm text-muted-foreground select-none">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
                    />
                    <span>{t('login.rememberMe', 'Remember me')}</span>
                  </label>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    {t('login.forgotPassword', 'Forgot password?')}
                  </Link>
                </div>
              )}

              <div className="flex justify-end mt-4">
                <Button
                  type="submit"
                  className="h-11 px-6"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>
                        {requires2FA 
                          ? (showBackupCodeInput ? 'Verifying backup code...' : 'Verifying OTP...')
                          : t('login.signingIn')
                        }
                      </span>
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      {passwordValidated && requires2FA
                        ? (showBackupCodeInput ? 'Verify Backup Code' : 'Verify OTP')
                        : t('login.signIn')
                      }
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </div>

          <div className="mt-6 text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} Selektia. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  )
}
